import { cn } from "@/lib/utils";

interface TypographyProps {
  weight?: "normal" | "medium" | "semibold" | "bold";
  colorVariant?:
    | "black-1"
    | "gray-1"
    | "gray-3"
    | "gray-4"
    | "primary"
    | "white";
}

interface HeadingProps extends TypographyProps, React.ComponentProps<"h1"> {
  variant?: "h1" | "h2" | "h3" | "h4" | "h5";
  asSpan?: boolean;
}

const colorVariantMap = {
  "black-1": "text-black-1",
  "gray-1": "text-gray-1",
  "gray-3": "text-gray-3",
  "gray-4": "text-gray-4",
  primary: "text-primary",
  white: "text-white",
};

const fontWeightMap = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

const headingSizeMap = {
  h1: "text-4xl sm:text-5xl",
  h2: "text-3xl sm:text-4xl",
  h3: "text-2xl sm:text-3xl",
  h4: "text-2xl sm:text-3xl",
  h5: "text-xl sm:text-2xl",
};

export const Heading = ({
  children,
  weight = "normal",
  variant = "h1",
  colorVariant = "black-1",
  asSpan = false,
  className,
  ...props
}: HeadingProps) => {
  const Element = asSpan ? "span" : variant;
  return (
    <Element
      className={cn(
        `tracking-[-0.03em] leading-[1.15] ${headingSizeMap[variant]} ${fontWeightMap[weight]} ${colorVariantMap[colorVariant]}`,
        className,
      )}
      {...props}
    >
      {children}
    </Element>
  );
};

interface TextProps extends TypographyProps, React.ComponentProps<"p"> {
  element?: "p" | "span";
  variant?: "text1" | "text2" | "text3" | "caption";
  italic?: boolean;
}

const textSizeMap = {
  text1: "text-xl",
  text2: "text-lg",
  text3: "text-md",
  caption: "text-sm",
};

export const Text = ({
  children,
  weight = "normal",
  variant = "text1",
  element = "p",
  colorVariant = "black-1",
  italic = false,
  className,
  ...props
}: TextProps) => {
  const Element = element;

  return (
    <Element
      className={cn(
        `${textSizeMap[variant]} ${fontWeightMap[weight]} ${colorVariantMap[colorVariant]} ${italic ? "italic" : ""}`,
        className,
      )}
      {...props}
    >
      {children}
    </Element>
  );
};
